import { useState } from "react";
import { useT } from "../../i18n/index.js";

/**
 * F-admin-usuarios (CEO 2026-11-02) — resultado de "criar conta" / "enviar link
 * de senha": diz para onde o e-mail foi e, quando o servidor devolve o link
 * (conta ainda não usada), oferece "Copiar link" para mandar por WhatsApp.
 */
export interface SenhaLinkResultado {
  titulo: string;
  nome: string;
  email: string;
  emailEnviado: boolean;
  link: string | null;
}

export function SenhaLinkModal({
  resultado,
  onClose,
}: {
  resultado: SenhaLinkResultado;
  onClose: () => void;
}) {
  const t = useT();
  const [copiado, setCopiado] = useState(false);
  const copiar = async () => {
    if (!resultado.link) return;
    try {
      await navigator.clipboard.writeText(resultado.link);
      setCopiado(true);
    } catch {
      // Sem permissão de área de transferência: o campo fica selecionado.
      (document.getElementById("senha-link-input") as HTMLInputElement | null)?.select();
    }
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-md space-y-3 rounded-xl border border-[var(--border-strong)] bg-[var(--surface-card)] p-5 shadow-[var(--shadow-sm)]"
        onClick={(e) => e.stopPropagation()}
        data-testid="user-link-box"
      >
        <h2 className="text-lg font-bold text-[var(--text-strong)]">{resultado.titulo}</h2>
        <p className="text-sm text-[var(--text-body)]">
          <strong>{resultado.nome}</strong>
        </p>
        {resultado.emailEnviado ? (
          <p className="text-sm text-[var(--text-body)]" data-testid="user-link-sent">
            ✉ {t("users.linkResultSent")} <strong className="break-all">{resultado.email}</strong>
          </p>
        ) : (
          <p
            className="rounded-md border border-[var(--warning-500)] bg-[var(--warning-50)] px-3 py-2 text-sm text-[var(--text-strong)]"
            data-testid="user-link-failed"
          >
            {t("users.linkResultFailed")}
          </p>
        )}
        {resultado.link ? (
          <div className="space-y-1.5">
            <p className="text-xs text-[var(--text-muted)]">{t("users.linkCopyHint")}</p>
            <div className="flex gap-2">
              <input
                id="senha-link-input"
                readOnly
                value={resultado.link}
                onFocus={(e) => e.currentTarget.select()}
                className="min-w-0 flex-1 rounded-md border border-[var(--border)] bg-[var(--surface-muted)] px-2 py-1.5 font-mono text-xs text-[var(--text-body)]"
              />
              <button
                type="button"
                onClick={() => void copiar()}
                className="shrink-0 rounded-md bg-lime px-3 py-1.5 text-sm font-medium text-on-lime"
                data-testid="user-link-copy"
              >
                {copiado ? t("users.copiedBtn") : t("users.copyLinkBtn")}
              </button>
            </div>
          </div>
        ) : (
          <p className="text-xs text-[var(--text-muted)]">{t("users.linkHiddenNote")}</p>
        )}
        <div className="flex justify-end pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-[var(--border-strong)] px-4 py-1.5 text-sm text-[var(--text-body)] hover:border-lime"
            data-testid="user-link-close"
          >
            {t("users.closeBtn")}
          </button>
        </div>
      </div>
    </div>
  );
}
